import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FiActivity, FiShoppingCart, FiCreditCard, FiPackage,
  FiDollarSign, FiRotateCcw, FiClock, FiExternalLink, FiInbox
} from 'react-icons/fi';
import { colors, spacing, borderRadius, typography } from '../../theme/colors';
import { cardVariants, listItemVariants } from '../../theme/animations';
import { formatRelativeDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import { useAuth } from '../../context/AuthContext';
import { getRecentTransactions } from '../../utils/dashboardWidgetData';
import {
  WidgetCard, WidgetHeader, WidgetTitle, WidgetSubtitle,
  WidgetActions, ActionButton, FlexColumn, ScrollContainer
} from './DashboardWidgets';

// tranTypeCode → display category (drives icon, color and +/- sign)
const CATEGORY_BY_CODE = {
  SI: 'sale', NSI: 'sale', PS: 'sale', SO: 'sale',
  PI: 'purchase', NPI: 'purchase', PO: 'purchase',
  CR: 'receipt', BR: 'receipt',
  CP: 'payment', BP: 'payment',
  SR: 'return', NSR: 'return', PR: 'return', NPR: 'return'
};

const categoryOf = (code) => CATEGORY_BY_CODE[code] || 'other';

const getTransactionIcon = (category) => {
  switch (category) {
    case 'sale': return <FiShoppingCart />;
    case 'purchase': return <FiPackage />;
    case 'payment': return <FiDollarSign />;
    case 'receipt': return <FiCreditCard />;
    case 'return': return <FiRotateCcw />;
    default: return <FiActivity />;
  }
};

const getTransactionColor = (category) => {
  switch (category) {
    case 'sale': return colors.success.main;
    case 'purchase': return colors.info.main;
    case 'payment': return colors.error.main;
    case 'receipt': return colors.success.main;
    case 'return': return colors.warning.main;
    default: return colors.primary.main;
  }
};

const getStatusColor = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'posted':
    case 'paid':
    case 'completed':
      return colors.success.main;
    case 'partial':
    case 'processing':
      return colors.info.main;
    case 'draft':
    case 'pending':
      return colors.warning.main;
    case 'cancelled':
    case 'void':
      return colors.error.main;
    default:
      return colors.neutral[500];
  }
};

const ActivityFeedWidget = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const { activeCompany } = useAuth();
  const companyId = activeCompany?.id;

  const [rows, setRows] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    if (!companyId) { setRows([]); return undefined; }
    setRows(null);
    (async () => {
      try {
        const txs = await getRecentTransactions(companyId);
        if (!cancelled) setRows(Array.isArray(txs) ? txs : []);
      } catch (e) {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  if (!companyId) return null;

  return (
    <WidgetCard
      variants={cardVariants}
      initial="initial"
      animate="animate"
      whileHover="hover"
    >
      <WidgetHeader>
        <FlexColumn $gap={spacing.xs}>
          <WidgetTitle>
            <FiActivity /> Recent Transactions
          </WidgetTitle>
          <WidgetSubtitle>The last 10 documents saved</WidgetSubtitle>
        </FlexColumn>
        <WidgetActions>
          <ActionButton onClick={() => navigate('/reports/journal')}>
            View More <FiExternalLink size={14} />
          </ActionButton>
        </WidgetActions>
      </WidgetHeader>

      {rows === null ? (
        <TransactionList>
          {[0, 1, 2, 3, 4].map(i => <SkelRow key={i} />)}
        </TransactionList>
      ) : rows.length === 0 ? (
        <EmptyBox>
          <FiInbox size={22} />
          <span>No transactions yet — new documents will show up here.</span>
        </EmptyBox>
      ) : (
        <ScrollContainer $maxHeight="280px">
          <TransactionList>
            {rows.map((t, index) => {
              const category = categoryOf(t.tranTypeCode);
              return (
                <TransactionItem
                  key={t.id}
                  variants={listItemVariants}
                  initial="initial"
                  animate="animate"
                  custom={index}
                >
                  <TransactionIcon $color={getTransactionColor(category)}>
                    {getTransactionIcon(category)}
                  </TransactionIcon>
                  <TransactionContent>
                    <TransactionHeader>
                      <TransactionId>{t.docNumber || t.id}</TransactionId>
                      {t.status && (
                        <TransactionStatus $color={getStatusColor(t.status)}>
                          {t.status}
                        </TransactionStatus>
                      )}
                    </TransactionHeader>
                    <TransactionParty>{t.partyName || t.tranTypeName || 'No party'}</TransactionParty>
                    <TransactionMeta>
                      <TransactionType $color={getTransactionColor(category)}>
                        {t.tranTypeCode || 'DOC'}
                      </TransactionType>
                      <TransactionTime>
                        <FiClock size={10} />
                        {formatRelativeDate(t.createdAt || t.docDate)}
                      </TransactionTime>
                    </TransactionMeta>
                  </TransactionContent>
                  <TransactionAmount $category={category}>
                    {category === 'payment' || category === 'purchase' ? '-' : '+'}
                    {formatCurrency(t.totalAmount || 0)}
                  </TransactionAmount>
                </TransactionItem>
              );
            })}
          </TransactionList>
        </ScrollContainer>
      )}
    </WidgetCard>
  );
};

// Styled Components
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const TransactionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

const TransactionItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.md};
  padding: ${spacing.md};
  background: var(--color-background-paper);
  border-radius: ${borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-neutral-100);
  }
`;

const TransactionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.round};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
`;

const TransactionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const TransactionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  margin-bottom: 2px;
`;

const TransactionId = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TransactionStatus = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: ${borderRadius.sm};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  text-transform: capitalize;
  flex-shrink: 0;
`;

const TransactionParty = styled.div`
  font-size: ${typography.fontSize.sm};
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TransactionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  margin-top: 4px;
`;

const TransactionType = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.$color};
`;

const TransactionTime = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${typography.fontSize.xs};
  color: var(--color-text-tertiary);
`;

const TransactionAmount = styled.div`
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.bold};
  white-space: nowrap;
  color: ${props =>
    props.$category === 'sale' || props.$category === 'receipt'
      ? 'var(--color-success-main)'
      : props.$category === 'return'
        ? 'var(--color-warning-main)'
        : props.$category === 'payment' || props.$category === 'purchase'
          ? 'var(--color-error-main)'
          : 'var(--color-text-primary)'
  };
`;

const SkelRow = styled.div`
  height: 64px;
  border-radius: ${borderRadius.md};
  background: ${colors.neutral[100]};
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  padding: 40px 16px;
  text-align: center;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: var(--color-text-secondary);

  svg { opacity: 0.5; }
`;

export default ActivityFeedWidget;
